/*
 * Assign flow model — V1.9.1 (design handoff README §The flows → A. Assign a
 * place; App Shell.dc.html assignVals is the measured reference). Pure — the
 * screen (AssignFlow.tsx) renders these; Vitest proves them against the
 * handoff + QA-CHECKLIST contract.
 *
 * THE TICKET'S AGE BAND DECIDES WHICH ROUTES EXIST (PRODUCT-RULES §Children /
 * §Adult tickets):
 *   band entirely under the signing age (Kids 5–12) → My child · Someone
 *     else's child — "Myself" and generic "Someone else" are NOT lawful
 *     holders and are not offered.
 *   band spanning the signing age (Open 16–99)      → Myself — {name} ·
 *     Someone else — the child language is not offered.
 *   Adult → Someone else asks the AGE BRACKET (18 or over / Under 18), NEVER
 *     a date of birth — the purchaser often doesn't know it and it belongs on
 *     the participant's own profile.
 *
 * DYNAMIC STEP LIST (measured from the prototype's assignVals):
 *   me                                   → who · confirm
 *   child (my child, kids band)          → who · details · confirm
 *   guest (someone else's child)         → who · permission · details · confirm
 *   other + asksAgeBracket (spanning band OR absent band — safe default):
 *     bracket adult                      → who · bracket · details · confirm
 *     bracket minor + rel mine           → who · bracket · relation · details · confirm
 *     bracket minor + rel other          → who · bracket · relation · permission · details · confirm
 *   other, fully-adult band (min≥signing) → who · details · confirm
 *
 * SINGLE-CHOICE STEPS (who / bracket / relation / permission) COMMIT ON TAP —
 * no Continue button (QA §10). Field steps keep Back + Continue with the
 * blocked-reason contract (QA §9): specific, ordered to the on-screen field
 * order, never naming a satisfied field.
 *
 * DRAFT SCOPING: answers are stamped with the seat
 * (`{orderRef}|{ticketIndex}.{seatIndex}` — ordersView.seatKeyFor) and reset
 * on open; changing route clears the previous route's answers (QA 8.8).
 */
import type { TicketEligibility } from '../../data/eligibility'
import type { TranslateFn } from '../../i18n/useLocale'
import type { ChildRecord } from './assignStore'

// ---------------------------------------------------------------------------
// Policy — organiser ticket-type band + jurisdiction signing age
// ---------------------------------------------------------------------------

export interface AgeBand { min: number; max: number }

export interface TicketPolicy {
  /** Age band of the ticket type — organiser-configured in manage.trumin.
   *  null ⇒ unbanded (adult email-invite path only). */
  band: AgeBand | null
  /** Legal signing age for the EVENT's jurisdiction. */
  signingAge: number
}

/** // TODO WIRE: signing age from the event's JURISDICTION config — 18 is the
 *  //   launch-market default, not a universal constant. */
export const DEFAULT_SIGNING_AGE = 18

/**
 * Resolve the ticket's policy from the R1 eligibility contract box-office
 * denormalizes onto /consumer/me/orders (ageMin/ageMax). A ticket without a
 * complete band resolves unbanded — bands are NEVER inferred from the type
 * NAME in shipped code (carry identifiers, never re-derive).
 */
export function resolveTicketPolicy(eligibility?: TicketEligibility | null): TicketPolicy {
  const band =
    eligibility && eligibility.ageMin != null && eligibility.ageMax != null
      ? { min: eligibility.ageMin, max: eligibility.ageMax }
      : null
  return { band, signingAge: DEFAULT_SIGNING_AGE }
}

/** Band entirely under the signing age (e.g. Kids 5–12). */
export const minorOnly = (p: TicketPolicy): boolean => p.band !== null && p.band.max < p.signingAge
/** Band spanning the signing age (e.g. Open 16–99). */
export const minorPossible = (p: TicketPolicy): boolean => p.band !== null && p.band.min < p.signingAge

/**
 * Does the "someone else" route ASK the under-18 / 18-or-over bracket rather
 * than assume an adult email invite? TRUE for a band spanning the signing age
 * (a minor is genuinely possible) AND for an ABSENT band — the boardroom-
 * ratified SAFE DEFAULT: with no band the ticket may still be a child/teen one,
 * so we ASK the bracket instead of silently routing every "someone else" as an
 * adult (the old no-band behaviour). FALSE only for a fully-adult band
 * (min ≥ signing age), where every lawful holder is an adult and the bracket
 * would be a pointless question.
 */
export const asksAgeBracket = (p: TicketPolicy): boolean =>
  p.band === null || p.band.min < p.signingAge

// ---------------------------------------------------------------------------
// Gender eligibility (CMT-2026-08-11-010806 — self-assign guard; data logic
// preserved verbatim from the pre-V1.9.1 model)
// ---------------------------------------------------------------------------

/** Binary-gender token sets. Exact-token match only (never substring) — a
 *  typeName token must BE one of these, so "Mixed"/"Open"/"PRO" never match. */
const FEMALE_TOKENS = new Set(['female', 'women', 'woman'])
const MALE_TOKENS = new Set(['male', 'men', 'man'])

/** The ticket's binary gender restriction parsed from its typeName, or null.
 *  Splits on '·', trims + lowercases each token, and returns 'female'/'male'
 *  ONLY when a token is EXACTLY a binary gender. Open / Mixed / absent / a
 *  contradictory mix (both a male AND a female token) → null (unrestricted). */
export function ticketGenderRestriction(typeName: string): 'male' | 'female' | null {
  if (!typeName) return null
  const tokens = typeName.split('·').map((t) => t.trim().toLowerCase())
  const female = tokens.some((t) => FEMALE_TOKENS.has(t))
  const male = tokens.some((t) => MALE_TOKENS.has(t))
  if (female && male) return null // contradictory → treat as unrestricted
  if (female) return 'female'
  if (male) return 'male'
  return null
}

/** Is the participant's own gender clearly the OPPOSITE binary of the ticket's
 *  restriction? Returns a participant-facing reason ONLY for a clear mismatch;
 *  everything else (unrestricted ticket, or unknown / empty / self-described /
 *  non-binary participant gender) allows. */
export function genderEligible(
  participantGender: string | null | undefined,
  typeName: string,
): { ok: boolean; reason?: string } {
  const restriction = ticketGenderRestriction(typeName)
  if (!restriction) return { ok: true }

  const g = (participantGender ?? '').trim().toLowerCase()
  if (!g) return { ok: true } // unknown → never block (conservative)
  const participantFemale = FEMALE_TOKENS.has(g)
  const participantMale = MALE_TOKENS.has(g)
  if (!participantFemale && !participantMale) return { ok: true } // self-describe / non-binary / other

  if (restriction === 'female' && participantMale) {
    return {
      ok: false,
      reason: "This ticket is for female participants; your profile gender doesn't match. Contact the organiser if this is wrong.",
    }
  }
  if (restriction === 'male' && participantFemale) {
    return {
      ok: false,
      reason: "This ticket is for male participants; your profile gender doesn't match. Contact the organiser if this is wrong.",
    }
  }
  return { ok: true }
}

// ---------------------------------------------------------------------------
// Event-owner child-field configuration
// ---------------------------------------------------------------------------

export interface ChildFieldConfig {
  /** Whether the add-a-child form REQUIRES the date of birth. */
  childDob: 'Required' | 'Optional'
  /** Whether gender is asked, and whether it blocks. */
  childGender: 'Required' | 'Optional' | 'Not asked'
  /** The event's gender policy — drives the child gender option set. */
  genderPolicy: 'binary' | 'flagged' | 'third'
}

/** // TODO WIRE: event-owner configuration source (manage.trumin ticket-type /
 *  //   event config on the orders payload). Defaults: DOB required, gender
 *  //   optional, binary options. */
export const DEFAULT_CHILD_FIELD_CONFIG: ChildFieldConfig = {
  childDob: 'Required',
  childGender: 'Optional',
  genderPolicy: 'binary',
}

export function genderOptions(policy: ChildFieldConfig['genderPolicy']): string[] {
  return {
    binary: ['Female', 'Male'],
    flagged: ['Female', 'Male', 'Prefer to self-describe'],
    third: ['Women', 'Men', 'Open'],
  }[policy]
}

// ---------------------------------------------------------------------------
// Draft (stamped with the seat)
// ---------------------------------------------------------------------------

export type AssignStepKey = 'who' | 'bracket' | 'relation' | 'permission' | 'details' | 'confirm'
export type AssignMode = 'me' | 'other' | 'child' | 'guest'
export type Bracket = 'adult' | 'minor'
export type Relation = 'mine' | 'other'
export type Permission = 'have' | 'parent'

export interface AssignDraft {
  /** The seat this draft belongs to — `{orderRef}|{ticketIndex}.{seatIndex}`
   *  (ordersView.seatKeyFor). Reset AND stamped on open. */
  seatKey: string
  mode: AssignMode | ''
  bracket: Bracket | ''
  rel: Relation | ''
  perm: Permission | ''
  // email-invite details (other-adult / guestparent)
  email: string
  name: string
  parentName: string
  // child picker / add-a-child form
  child: string
  addKid: boolean
  kidFirst: string
  kidLast: string
  kidDobD: string
  kidDobM: string
  kidDobY: string
  kidGender: string
  // teen view-only access (my-teenager route only)
  teenEmail: string
}

/** Fresh draft for a seat — reset AND stamped (draft-scoping rule). */
export function emptyDraft(seatKey: string): AssignDraft {
  return {
    seatKey,
    mode: '', bracket: '', rel: '', perm: '',
    email: '', name: '', parentName: '',
    child: '', addKid: false,
    kidFirst: '', kidLast: '', kidDobD: '', kidDobM: '', kidDobY: '', kidGender: '',
    teenEmail: '',
  }
}

/** Route-change patches — picking an option CLEARS everything downstream of it
 *  so a stale answer from an abandoned route can never apply (QA 8.8). */
export const pickMode = (mode: AssignMode): Partial<AssignDraft> =>
  ({ mode, bracket: '', rel: '', perm: '', child: '', name: '', addKid: false, kidFirst: '', kidLast: '', kidDobD: '', kidDobM: '', kidDobY: '', kidGender: '', teenEmail: '', email: '', parentName: '' })
export const pickBracket = (bracket: Bracket): Partial<AssignDraft> =>
  ({ bracket, rel: '', perm: '', child: '', name: '', addKid: false, kidFirst: '', kidLast: '', kidDobD: '', kidDobM: '', kidDobY: '', kidGender: '', teenEmail: '', email: '', parentName: '' })
export const pickRelation = (rel: Relation): Partial<AssignDraft> =>
  ({ rel, perm: '', child: '', name: '', addKid: false, kidFirst: '', kidLast: '', kidDobD: '', kidDobM: '', kidDobY: '', kidGender: '', teenEmail: '', email: '', parentName: '' })
export const pickPermission = (perm: Permission): Partial<AssignDraft> =>
  ({ perm, child: '', name: '', addKid: false, kidFirst: '', kidLast: '', kidDobD: '', kidDobM: '', kidDobY: '', kidGender: '', teenEmail: '', email: '', parentName: '' })

// ---------------------------------------------------------------------------
// Route flags
// ---------------------------------------------------------------------------

export interface RouteFlags {
  /** Adult ticket → Someone else → Under-18 bracket. */
  teen: boolean
  /** The seat's holder is a child/teen the PURCHASER signs for (my child, or
   *  my teenager). */
  routeChild: boolean
  /** Someone else's child/teenager. */
  routeGuest: boolean
  /** Guest route where the PARENT signs (email invite → pending). */
  guestParent: boolean
  /** The details step is a child picker (own pool, or guest pool with stated
   *  permission). */
  pickChild: boolean
  /** Wording: kids-band routes say "child"; the adult-ticket under-18 routes
   *  say "teenager" — in EVERY message (QA 9.4). */
  noun: 'child' | 'teenager'
}

export function routeFlags(d: AssignDraft): RouteFlags {
  const teen = d.mode === 'other' && d.bracket === 'minor'
  const routeChild = d.mode === 'child' || (teen && d.rel === 'mine')
  const routeGuest = d.mode === 'guest' || (teen && d.rel === 'other')
  const guestParent = routeGuest && d.perm === 'parent'
  const pickChild = routeChild || (routeGuest && d.perm === 'have')
  return { teen, routeChild, routeGuest, guestParent, pickChild, noun: teen ? 'teenager' : 'child' }
}

// ---------------------------------------------------------------------------
// Step machine
// ---------------------------------------------------------------------------

export function stepList(d: AssignDraft, p: TicketPolicy): AssignStepKey[] {
  if (d.mode === 'me') return ['who', 'confirm']
  if (d.mode === 'guest') return ['who', 'permission', 'details', 'confirm']
  if (d.mode === 'child') return ['who', 'details', 'confirm']
  // A spanning band OR an ABSENT band asks the bracket (safe default — never
  // silently-adult); a fully-adult band skips straight to the email invite.
  if (d.mode === 'other' && asksAgeBracket(p)) {
    if (d.bracket === 'minor') {
      return d.rel === 'other'
        ? ['who', 'bracket', 'relation', 'permission', 'details', 'confirm']
        : ['who', 'bracket', 'relation', 'details', 'confirm']
    }
    return ['who', 'bracket', 'details', 'confirm']
  }
  return ['who', 'details', 'confirm']
}

/** Single-choice steps commit on tap: Back + quiet "Tap an option to
 *  continue", NO Continue button (QA §10). */
export const isChoiceStep = (cur: AssignStepKey): boolean =>
  cur === 'who' || cur === 'bracket' || cur === 'relation' || cur === 'permission'

// ---------------------------------------------------------------------------
// Age on race day (day / month-NAME / year selects — never a numeric format)
// ---------------------------------------------------------------------------

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export function ageOn(dobD: string, dobM: string, dobY: string, raceDay: Date): number | null {
  if (!dobD || !dobM || !dobY) return null
  // Unrecognized month → null (conservative-allow at the band gate), NEVER
  // Date(y, -1, d)'s silent prior-December wrong age — this output now feeds
  // selectedKidInBand, not just the picker note (wizardModel.ageFromDob prior art).
  const mi = MONTHS.indexOf(dobM)
  if (mi < 0) return null
  const b = new Date(Number(dobY), mi, Number(dobD))
  return (
    raceDay.getFullYear() - b.getFullYear() -
    (raceDay.getMonth() < b.getMonth() ||
    (raceDay.getMonth() === b.getMonth() && raceDay.getDate() < b.getDate())
      ? 1
      : 0)
  )
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
export const emailOk = (v: string): boolean => EMAIL_RE.test(v.trim())

// ---------------------------------------------------------------------------
// Derived DOB window (BOARDROOM-2026-09-01-202244 WU4 + CMT-2026-09-02-002)
// ---------------------------------------------------------------------------

/**
 * The EFFECTIVE age window the seat's holder must land in on race day.
 *   Kids / guest-child routes (teen=false) → the raw band; a null band means
 *     NO window — unbanded never constrains on this axis (matching kidInBand's
 *     long-standing conservative-allow).
 *   TEEN routes (adult/spanning ticket → Under-18 branch, teen=true) → the
 *     band ∩ under-signing-age: [band.min .. min(band.max, signingAge−1)].
 *     A 16–99/18 ticket's teen window is 16–17 — every verdict/refusal must
 *     SAY 16–17, never the confusing raw 16–99 (a teen on this route can
 *     never lawfully be 18+; the purchaser chose "Under 18" one step back).
 *     With a NULL band the under-18 bracket choice is the ONLY constraint
 *     that exists, so the teen window is [0 .. signingAge−1] — we deliberately
 *     do NOT invent an age floor the organiser never configured.
 * The teen route only exists where asksAgeBracket holds (band null or
 * band.min < signingAge), so a teen window is never empty.
 */
export function effectiveWindow(p: TicketPolicy, teen: boolean): AgeBand | null {
  if (!teen) return p.band
  const cap = p.signingAge - 1
  return p.band
    ? { min: p.band.min, max: Math.min(p.band.max, cap) }
    : { min: 0, max: cap }
}

/**
 * Birth YEARS the DOB year <select> offers — derived from the REAL race day +
 * the ticket's resolved band, NEVER hardcoded (BOARDROOM-2026-09-01-202244
 * WU4). Someone born in year Y is either (raceYear−Y) or (raceYear−Y−1) on
 * race day, depending on whether their birthday has passed; Y is OFFERED iff
 * that two-age set intersects the effective window. So BOTH CUSP YEARS STAY:
 * a child born late in the youngest cusp year (or early in the oldest) is
 * still in-window on race day, and the exact-DOB validation (kidInBand →
 * blockText's band message) refuses the specific out-of-window day/month
 * combos. This boundary rule is DELIBERATE — stated to boardroom in
 * MY-2026-09-01-202633: the year list narrows honestly without ever hiding a
 * lawful birth year; exact enforcement stays with the full-DOB gate.
 *
 * Newest year first (the select's existing order). A null effective window
 * (unbanded, non-teen — unreachable through the shipped routes, since the
 * add form only renders on kids-band or teen routes) keeps the legacy
 * 25-year list. When raceDay is the no-wire-date "now" fallback
 * (pastEvents.raceDayFrom), the window still derives — an honest
 * approximation, same as the band checks themselves.
 */
export function pickableYears(p: TicketPolicy, teen: boolean, raceDay: Date): number[] {
  const raceYear = raceDay.getFullYear()
  const w = effectiveWindow(p, teen)
  if (!w) return Array.from({ length: 25 }, (_, i) => raceYear - i)
  const newest = raceYear - w.min // cusp: may yield w.min−1 for a late birthday
  const oldest = raceYear - w.max - 1 // cusp: may yield w.max+1 for an early birthday
  return Array.from({ length: newest - oldest + 1 }, (_, i) => newest - i)
}

// ---------------------------------------------------------------------------
// Child pools — TWO pools, never merged (PRODUCT-RULES §Children)
// ---------------------------------------------------------------------------

export interface ChildOption {
  key: string
  guest: boolean
  name: string
  /** Picker note: `Age {n} on race day · {gender}` — only what's worth
   *  reading; '' when nothing is. */
  note: string
  /** Recorded age on race day — carried so the band gate can judge an
   *  EXISTING child identically to a newly-entered one (the add/select
   *  asymmetry defect, QUALITY-2026-09-01-151532). null = DOB unknown. */
  ageOnRace: number | null
}

/** The picker only ever offers the pool matching the chosen route. */
export function pickerPool(
  children: ChildRecord[], flags: RouteFlags, raceDay: Date, tr: TranslateFn,
): ChildOption[] {
  return children
    .filter((k) => k.guest === flags.routeGuest)
    .map((k) => {
      const name = `${k.first} ${k.last}`.trim() || 'Child'
      const yrs = ageOn(k.dobD, k.dobM, k.dobY, raceDay)
      const bits = [
        yrs !== null ? tr('assign.addChild.ageOn', { age: yrs }) : '',
        k.gender || '',
      ].filter(Boolean)
      return { key: k.key, guest: k.guest, name, note: bits.join(' · '), ageOnRace: yrs }
    })
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export const kidDobComplete = (d: AssignDraft): boolean => !!(d.kidDobD && d.kidDobM && d.kidDobY)

export const kidAgeOnRaceDay = (d: AssignDraft, raceDay: Date): number | null =>
  ageOn(d.kidDobD, d.kidDobM, d.kidDobY, raceDay)

/** Band validation AT THE POINT OF ASSIGNMENT: an incomplete DOB or an
 *  absent effective window never blocks on this axis. Judges against the
 *  EFFECTIVE window (band ∩ under-signing on the teen routes — derived from
 *  the draft's own route), not the raw band: an 18+ DOB on the Under-18
 *  branch is refused even when the raw band (16–99) would admit it. */
export function kidInBand(d: AssignDraft, p: TicketPolicy, raceDay: Date): boolean {
  const w = effectiveWindow(p, routeFlags(d).teen)
  if (!kidDobComplete(d) || !w) return true
  const age = kidAgeOnRaceDay(d, raceDay)
  return age !== null && age >= w.min && age <= w.max
}

/** ONE band gate for BOTH selection paths (QUALITY-2026-09-01-151532 /
 *  BOARDROOM-2026-09-01-152219: the add-new path blocked out-of-band but
 *  selecting an EXISTING managed child bypassed the check entirely). The
 *  draft DOB judges an added child; the chosen record's age judges an
 *  existing one. Unknown DOB / unbanded ticket conservative-allows on this
 *  axis (matching kidInBand). HONESTY: on the child/guestchild routes the
 *  outcome is client-recorded (no server write — liveSubmit short-circuits),
 *  so conservative-allow has NO server backstop until the child-holder
 *  contract seam lands; with childDob Required (the shipped config) records
 *  always carry DOBs, so the unbackstopped case is unreachable today. No
 *  selection yet is not a band problem. */
export function selectedKidInBand(
  d: AssignDraft, p: TicketPolicy, pool: ChildOption[], raceDay: Date, adding: boolean,
): boolean {
  if (adding) return kidInBand(d, p, raceDay)
  // Same EFFECTIVE window as the add path (band ∩ under-signing on teen
  // routes) — an existing 19-year-old record is refused on the Under-18
  // branch just like a typed 19-year-old DOB.
  const w = effectiveWindow(p, routeFlags(d).teen)
  if (!w || !d.child) return true
  const age = pool.find((k) => k.key === d.child)?.ageOnRace ?? null
  return age === null || (age >= w.min && age <= w.max)
}

/** Age verdict box under the DOB selects. */
export function kidAgeVerdict(
  d: AssignDraft, p: TicketPolicy, raceDay: Date, ticketType: string, tr: TranslateFn,
): { text: string; tone: 'ok' | 'bad' } | null {
  if (!kidDobComplete(d)) return null
  const age = kidAgeOnRaceDay(d, raceDay)
  if (age === null) return null
  if (!kidInBand(d, p, raceDay)) {
    // kidInBand false ⇒ an effective window exists. The verdict names the
    // EFFECTIVE bounds — on the teen route that is band ∩ under-signing
    // (16–17 for a 16–99/18 ticket), never the raw band's confusing 16–99.
    const w = effectiveWindow(p, routeFlags(d).teen)!
    return { text: tr('assign.kidAge.bad', { age, min: w.min, max: w.max }), tone: 'bad' }
  }
  return {
    text: p.band
      ? tr('assign.kidAge.ok', { age, ticket: ticketType })
      : tr('assign.kidAge.okNoBand', { age }),
    tone: 'ok',
  }
}

export function newKidOk(d: AssignDraft, p: TicketPolicy, cfg: ChildFieldConfig, raceDay: Date): boolean {
  return (
    !!d.kidFirst.trim() && !!d.kidLast.trim() &&
    (cfg.childDob !== 'Required' || kidDobComplete(d)) &&
    kidInBand(d, p, raceDay) &&
    (cfg.childGender !== 'Required' || !!d.kidGender)
  )
}

/** The teen view-only card appears only on the my-teenager route, once a
 *  SPECIFIC teenager is chosen or named — the address belongs to them. */
export function teenName(d: AssignDraft, pool: ChildOption[], adding: boolean): string {
  return adding
    ? `${d.kidFirst.trim()} ${d.kidLast.trim()}`.trim()
    : pool.find((k) => k.key === d.child)?.name ?? ''
}

export function teenCardVisible(d: AssignDraft, flags: RouteFlags, pool: ChildOption[], adding: boolean): boolean {
  return flags.routeChild && flags.teen && !!teenName(d, pool, adding)
}

/** Supplying a VALID address IS the opt-in — no separate switch. */
export function wantsTeenLogin(d: AssignDraft, flags: RouteFlags, pool: ChildOption[], adding: boolean): boolean {
  return teenCardVisible(d, flags, pool, adding) && emailOk(d.teenEmail)
}

/** Can the current step continue? (Choice steps gate their tap-advance; field
 *  steps gate Continue/confirm.) */
export function stepOk(
  cur: AssignStepKey, d: AssignDraft, p: TicketPolicy, cfg: ChildFieldConfig,
  pool: ChildOption[], raceDay: Date,
): boolean {
  const flags = routeFlags(d)
  const adding = d.addKid || pool.length === 0
  switch (cur) {
    case 'who': return !!d.mode
    case 'bracket': return !!d.bracket
    case 'relation': return !!d.rel
    case 'permission': return !!d.perm
    case 'details': {
      const teenEmailOk = !teenCardVisible(d, flags, pool, adding) || !d.teenEmail.trim() || emailOk(d.teenEmail)
      // newKidOk already folds kidInBand for the add path; the shared gate
      // makes the EXISTING-child selection answer the same band question.
      const core = flags.pickChild
        ? adding
          ? newKidOk(d, p, cfg, raceDay)
          : !!d.child && selectedKidInBand(d, p, pool, raceDay, adding)
        : emailOk(d.email)
      return core && teenEmailOk
    }
    default: return true
  }
}

/** Blocked-reason bar text — SPECIFIC missing field, ordered to the on-screen
 *  field order, never a satisfied field (QA §9). Only field steps surface it
 *  (choice steps advance on tap and carry no primary action to block). */
export function blockText(
  cur: AssignStepKey, d: AssignDraft, p: TicketPolicy, cfg: ChildFieldConfig,
  pool: ChildOption[], raceDay: Date, tr: TranslateFn,
): string {
  const flags = routeFlags(d)
  const adding = d.addKid || pool.length === 0
  const teen = flags.noun === 'teenager'
  if (cur !== 'details') return ''
  if (flags.pickChild) {
    if (!adding && !d.child) return tr(teen ? 'assign.block.chooseTeen' : 'assign.block.chooseChild')
    if (adding && (!d.kidFirst.trim() || !d.kidLast.trim())) {
      return tr(teen ? 'assign.block.teenNames' : 'assign.block.childNames')
    }
    if (adding && cfg.childDob === 'Required' && !kidDobComplete(d)) {
      return tr(teen ? 'assign.block.teenDob' : 'assign.block.childDob')
    }
    // SAME band message on BOTH paths — the shared gate covers the existing-
    // child selection too (QUALITY-2026-09-01-151532). Bounds are the
    // EFFECTIVE window (false gate ⇒ it exists): "They must be 16–17…" on a
    // 16–99/18 ticket's teen route, never the raw 16–99.
    if (!selectedKidInBand(d, p, pool, raceDay, adding)) {
      const w = effectiveWindow(p, flags.teen)!
      return tr('assign.block.band', { min: w.min, max: w.max })
    }
    if (adding && cfg.childGender === 'Required' && !d.kidGender) {
      return tr(teen ? 'assign.block.teenGender' : 'assign.block.childGender')
    }
    // Only the (optional) teen view-only email can still be blocking here —
    // present but invalid.
    return tr('assign.block.teenEmail')
  }
  if (flags.guestParent) return tr('assign.block.parentEmail')
  return tr('assign.block.email')
}

// ---------------------------------------------------------------------------
// Chrome copy — headings, intros, eyebrows, choice sets
// ---------------------------------------------------------------------------

export interface AssignCtx {
  orderRef: string
  /** 0-based index of the ticket within its order + the order's ticket count
   *  (`TICKET n OF m` eyebrow — 1-based for display). */
  ticketIndex: number
  nTickets: number
  ticketType: string
  evName: string
  seatNo: number
  of: number
  /** Current holder label when re-assigning a filled seat ('' = fresh). */
  holder: string
  myName: string
  myEmail: string
  /** Signed-in account holder's Cognito `gender` (self-assign eligibility). */
  myGender?: string
}

/** Step-1 eyebrow: `TICKET n OF m` (+ ` · PERSON x OF y`) — uppercase is
 *  applied by the eyebrow style. */
export function whoEyebrow(ctx: AssignCtx, tr: TranslateFn): string {
  return ctx.of > 1
    ? tr('assign.eyebrow.ticketPerson', { n: ctx.ticketIndex + 1, m: ctx.nTickets, x: ctx.seatNo, y: ctx.of })
    : tr('assign.eyebrow.ticket', { n: ctx.ticketIndex + 1, m: ctx.nTickets })
}

export function eyebrow(cur: AssignStepKey, ctx: AssignCtx, tr: TranslateFn): string {
  switch (cur) {
    case 'who': return whoEyebrow(ctx, tr)
    case 'bracket': return tr('assign.eyebrow.bracket')
    case 'relation': return tr('assign.eyebrow.relation')
    case 'permission': return tr('assign.eyebrow.permission')
    case 'details': return tr('assign.eyebrow.details')
    default: return tr('assign.eyebrow.confirm')
  }
}

export function heading(
  cur: AssignStepKey, d: AssignDraft, ctx: AssignCtx, adding: boolean, tr: TranslateFn,
): string {
  const flags = routeFlags(d)
  switch (cur) {
    case 'who': return ctx.ticketType
    case 'bracket': return tr('assign.bracket.heading')
    case 'relation': return tr('assign.relation.heading')
    case 'permission': return tr('assign.permission.heading')
    case 'details':
      if (flags.guestParent) return tr('assign.details.heading.parentEmail')
      if (flags.pickChild) {
        if (adding) return tr(flags.noun === 'teenager' ? 'assign.details.heading.addTeen' : 'assign.details.heading.addChild')
        return tr(flags.noun === 'teenager' ? 'assign.details.heading.whichTeen' : 'assign.details.heading.whichChild')
      }
      return tr('assign.details.heading.invite')
    default:
      if (d.mode === 'me') return tr('assign.confirm.heading.me')
      return flags.pickChild ? tr('assign.confirm.heading.child') : tr('assign.confirm.heading.send')
  }
}

export function intro(
  cur: AssignStepKey, d: AssignDraft, p: TicketPolicy, tr: TranslateFn,
): string {
  const flags = routeFlags(d)
  switch (cur) {
    case 'who': return ''
    case 'bracket': return tr('assign.bracket.intro', { age: p.signingAge })
    case 'relation': return tr('assign.relation.intro')
    case 'permission': return tr('assign.permission.intro')
    case 'details':
      if (flags.guestParent) return tr('assign.details.intro.guestParent')
      if (flags.routeGuest && d.perm === 'have') return tr('assign.details.intro.guestHave')
      if (flags.routeChild) return tr('assign.details.intro.child')
      return tr('assign.details.intro.invite')
    default:
      if (d.mode === 'me') return tr('assign.confirm.intro.me')
      if (flags.pickChild && flags.teen) return tr('assign.confirm.intro.teenChild')
      if (flags.pickChild) return tr('assign.confirm.intro.child')
      return tr('assign.confirm.intro.other')
  }
}

export interface WhoChoice { mode: AssignMode; label: string; icon: string }

/** The who-step choice set — decided by the TICKET's band, never the UI:
 *  kids band → My child / Someone else's child; else → Myself / Someone else. */
export function whoChoices(p: TicketPolicy, myName: string, tr: TranslateFn): WhoChoice[] {
  if (minorOnly(p)) {
    return [
      { mode: 'child', label: tr('assign.choice.child'), icon: '◈' },
      { mode: 'guest', label: tr('assign.choice.guest'), icon: '◇' },
    ]
  }
  return [
    { mode: 'me', label: tr('assign.choice.me', { name: myName }), icon: '◉' },
    { mode: 'other', label: tr('assign.choice.other'), icon: '✉' },
  ]
}

export interface TaggedChoice<K extends string> { k: K; title: string; note: string; tag: string }

export function bracketChoices(p: TicketPolicy, tr: TranslateFn): Array<TaggedChoice<Bracket>> {
  return [
    {
      k: 'adult',
      title: tr('assign.bracket.adult.title', { age: p.signingAge }),
      note: tr('assign.bracket.adult.note'),
      tag: tr('assign.bracket.adult.tag'),
    },
    {
      k: 'minor',
      title: tr('assign.bracket.minor.title', { age: p.signingAge }),
      note: tr('assign.bracket.minor.note'),
      tag: tr('assign.bracket.minor.tag'),
    },
  ]
}

/** Plain A-or-B — no tags, no sub-notes (QA 10.4). */
export function relationChoices(tr: TranslateFn): Array<{ k: Relation; title: string }> {
  return [
    { k: 'mine', title: tr('assign.relation.mine') },
    { k: 'other', title: tr('assign.relation.other') },
  ]
}

export function permissionChoices(tr: TranslateFn): Array<TaggedChoice<Permission>> {
  return [
    { k: 'have', title: tr('assign.perm.have.title'), note: tr('assign.perm.have.note'), tag: tr('assign.perm.have.tag') },
    { k: 'parent', title: tr('assign.perm.parent.title'), note: tr('assign.perm.parent.note'), tag: tr('assign.perm.parent.tag') },
  ]
}

// ---------------------------------------------------------------------------
// Confirm + done
// ---------------------------------------------------------------------------

/** Who the seat is being assigned to, resolved from the draft. */
export function assigneeName(d: AssignDraft, ctx: AssignCtx, pool: ChildOption[], tr: TranslateFn): string {
  const flags = routeFlags(d)
  const adding = d.addKid || pool.length === 0
  if (d.mode === 'me') return ctx.myName
  if (flags.pickChild) return teenName(d, pool, adding)
  if (flags.guestParent) return d.name.trim() || tr('assign.row.parentsChild')
  return d.email.trim()
}

/**
 * Confirm-step rows — EXACTLY (QA 4.7): `Ticket n of m` → type, `Assigned to`
 * → person, plus `Parent` on the parent-signs route and `Age` / `Waiver &
 * payment` / `Their view-only login` on the teen route. No `Place`, no `How`.
 */
export function summaryRows(
  d: AssignDraft, ctx: AssignCtx, p: TicketPolicy, pool: ChildOption[], tr: TranslateFn,
): Array<{ k: string; v: string }> {
  const flags = routeFlags(d)
  const adding = d.addKid || pool.length === 0
  const ticketK = ctx.of > 1
    ? tr('assign.row.ticketPerson', { n: ctx.ticketIndex + 1, m: ctx.nTickets, x: ctx.seatNo, y: ctx.of })
    : tr('assign.row.ticket', { n: ctx.ticketIndex + 1, m: ctx.nTickets })
  const rows: Array<{ k: string; v: string } | null> = [
    { k: ticketK, v: ctx.ticketType },
    { k: tr('assign.row.assignedTo'), v: assigneeName(d, ctx, pool, tr) },
    flags.guestParent
      ? {
          k: tr('assign.row.parent'),
          v: (d.parentName.trim() ? `${d.parentName.trim()} · ` : '') + d.email.trim(),
        }
      : null,
    flags.teen ? { k: tr('assign.row.age'), v: tr('assign.row.age.value', { age: p.signingAge }) } : null,
    flags.teen && flags.routeChild
      ? { k: tr('assign.row.waiver'), v: tr('assign.row.waiver.value', { name: ctx.myName }) }
      : null,
    flags.teen && flags.routeChild && wantsTeenLogin(d, flags, pool, adding)
      ? { k: tr('assign.row.teenLogin'), v: d.teenEmail.trim() }
      : null,
  ]
  return rows.filter((r): r is { k: string; v: string } => r !== null)
}

/** What-happens-next line under the confirm card. */
export function nextNote(d: AssignDraft, pool: ChildOption[], tr: TranslateFn): string {
  const flags = routeFlags(d)
  const adding = d.addKid || pool.length === 0
  if (d.mode === 'me') return tr('assign.next.me')
  if (flags.routeGuest && d.perm === 'have') return tr('assign.next.guestHave')
  if (flags.guestParent) return tr('assign.next.guestParent')
  if (d.mode === 'child') return tr('assign.next.child')
  if (flags.teen && flags.routeChild) {
    return wantsTeenLogin(d, flags, pool, adding) ? tr('assign.next.teenLogin') : tr('assign.next.teenNoLogin')
  }
  return tr('assign.next.other')
}

/** Confirm button label. */
export function confirmLabel(d: AssignDraft, tr: TranslateFn): string {
  const flags = routeFlags(d)
  if (flags.guestParent) return tr('assign.cta.sendParent')
  if (flags.teen && flags.routeChild) return tr('assign.cta.signLater')
  if (d.mode === 'other' && !flags.teen) return tr('assign.cta.sendInvite')
  return tr('assign.cta.confirm')
}

// ---------------------------------------------------------------------------
// Outcome — handed to the submit seam at explicit confirm (never earlier)
// ---------------------------------------------------------------------------

/** Final seat mode vocabulary (handoff §State: me | child | guestchild |
 *  guestparent | other; the teen my-route records mode 'child' + minor). */
export type OutcomeMode = 'me' | 'other' | 'child' | 'guestchild' | 'guestparent'

export interface AssignOutcome {
  seatKey: string
  mode: OutcomeMode
  /** True when the seat already had a holder (replace semantics). */
  reassign: boolean
  /** Invite target: the other-adult's own email, or the PARENT's email on the
   *  guestparent route. '' on the non-invite routes. */
  email: string
  name: string
  parentName: string
  /** Teen my-route (adult ticket, under-18, my child) — you sign as guardian. */
  minor: boolean
  teenLogin: boolean
  teenEmail: string
  childKey: string
  /** New child to create in the store ('' childKey) — null when picking. */
  newChild: { first: string; last: string; dobD: string; dobM: string; dobY: string; gender: string; guest: boolean } | null
  childName: string
  tType: string
  evName: string
}

export function buildOutcome(
  d: AssignDraft, ctx: AssignCtx, pool: ChildOption[], reassign: boolean,
): AssignOutcome {
  const flags = routeFlags(d)
  const adding = d.addKid || pool.length === 0
  const childName = flags.pickChild ? teenName(d, pool, adding) : ''
  const guest = flags.routeGuest
  const mode: OutcomeMode =
    flags.guestParent ? 'guestparent'
      : flags.pickChild ? (guest ? 'guestchild' : 'child')
        : d.mode === 'me' ? 'me' : 'other'
  const teenLogin = wantsTeenLogin(d, flags, pool, adding)
  return {
    seatKey: d.seatKey,
    mode,
    reassign,
    email: mode === 'other' || mode === 'guestparent' ? d.email.trim() : '',
    name: mode === 'guestparent' ? d.name.trim() : childName || d.name.trim(),
    parentName: mode === 'guestparent' ? d.parentName.trim() : '',
    minor: flags.teen && flags.routeChild,
    teenLogin,
    teenEmail: teenLogin ? d.teenEmail.trim() : '',
    childKey: flags.pickChild && !adding ? d.child : '',
    newChild: flags.pickChild && adding
      ? {
          first: d.kidFirst.trim(), last: d.kidLast.trim(),
          dobD: d.kidDobD, dobM: d.kidDobM, dobY: d.kidDobY,
          gender: d.kidGender, guest,
        }
      : null,
    childName,
    tType: ctx.ticketType,
    evName: ctx.evName,
  }
}

// ---------------------------------------------------------------------------
// Done copy
// ---------------------------------------------------------------------------

export interface SendFlags {
  /** my-api's honest send flag for the email-invite routes. */
  inviteEmailSent: boolean
  /** Whether a teen view-only login email was actually sent (TODO WIRE — the
   *  client-state stub always reports false; the claim never renders). */
  teenLoginEmailSent: boolean
}

export function doneHeading(o: AssignOutcome, tr: TranslateFn): string {
  if (o.reassign) return tr('assign.done.reassigned')
  if (o.mode === 'guestparent') return tr('assign.done.parentInvited')
  if (o.mode === 'guestchild' || o.mode === 'child') return tr('assign.done.reserved')
  if (o.mode === 'other') return tr('assign.done.inviteSent')
  return tr('assign.done.assignedYou')
}

export function doneSub(o: AssignOutcome, sent: SendFlags, tr: TranslateFn): string {
  if (o.mode === 'guestparent') {
    return sent.inviteEmailSent
      ? tr('assign.done.sub.guestParent', { who: o.parentName || o.email, email: o.email })
      : tr('assign.done.sub.guestParentNotSent', { who: o.name || "The parent's child" })
  }
  if (o.mode === 'guestchild') {
    return tr('assign.done.sub.guestHave', { name: o.childName || 'They' })
  }
  if (o.mode === 'child' && o.minor) {
    // Teen my-route: the view-only-login claim renders ONLY when a login email
    // actually went out (honesty gate — the stubbed seam reports false).
    return o.teenLogin && sent.teenLoginEmailSent
      ? tr('assign.done.sub.teenLogin', { name: o.childName || 'They', email: o.teenEmail })
      : tr('assign.done.sub.teen', { name: o.childName || 'They' })
  }
  if (o.mode === 'child') {
    return tr('assign.done.sub.child', { name: o.childName || 'Your child' })
  }
  if (o.mode === 'other') {
    return sent.inviteEmailSent
      ? tr('assign.done.sub.other', { who: o.email })
      : tr('assign.done.sub.otherNotSent', { who: o.email })
  }
  return tr('assign.done.sub.me')
}

/** Done heading override for the honest invite-not-sent case (existing
 *  behaviour preserved: allocation succeeded; only the email claim is gated). */
export function doneHeadingWithFlags(o: AssignOutcome, sent: SendFlags, tr: TranslateFn): string {
  if (!o.reassign && o.mode === 'other' && !sent.inviteEmailSent) return tr('assign.done.ticketAssigned')
  return doneHeading(o, tr)
}
